import { type OperatorSlug, validate } from '@ev/shared'
import { useMemo } from 'react'
import { create } from 'zustand'
import {
  type ChargeCard,
  type ChargeCardId,
  ChargeCardWallet,
  EMPTY_WALLET,
  hasBuiltInEdits,
  mergeChargeCards,
} from './chargeCards'

/**
 * The driver's wallet, kept in this browser.
 *
 * It sits beside the app store rather than inside it because the app store is
 * defined as "only what the URL holds", and the URL codec in `urlState.ts` is
 * frozen - the parameter names appear in links people have already shared. A
 * wallet is not shareable anyway: a held card travels as the operator slugs it
 * ticked, so a link still shows a stranger the same map without telling them
 * which plastic the sender happens to carry.
 *
 * `localStorage` rather than a cookie or an account: the whole site is static
 * and has no sign-in, so the wallet is a local preference, and saying so in the
 * editor is the honest version of that. Every read goes through the runtype in
 * `chargeCards.ts` - the value is hand-editable, outlives the deploy that wrote
 * it, and is shared with every other script on the origin, which is three
 * reasons not to trust its shape.
 *
 * A quota or a security error on write is swallowed on purpose. Private-mode
 * Safari throws on the first `setItem`, and a wallet that works until the tab
 * closes is a better answer than a filter panel that throws when somebody
 * defines a card.
 */

const STORAGE_KEY = 'ev:charge-cards'

const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    // Blocked by a cookie policy. The wallet lives for this page load only.
    return null
  }
}

export const readWallet = (): ChargeCardWallet => {
  const raw = (() => {
    try {
      return storage()?.getItem(STORAGE_KEY) ?? null
    } catch {
      return null
    }
  })()
  if (raw === null) return EMPTY_WALLET

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return EMPTY_WALLET
  }

  const result = validate(ChargeCardWallet, parsed, { source: STORAGE_KEY })
  if (!result.ok) {
    console.warn(`charge cards: ignoring a stored wallet that does not validate - ${result.error}`)
    return EMPTY_WALLET
  }
  return result.value
}

const writeWallet = (wallet: ChargeCardWallet): void => {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(wallet))
  } catch {
    // Out of quota, or a private window. Nothing a driver can act on.
  }
}

/**
 * A fresh card id.
 *
 * `randomUUID` needs a secure context, which the live site is and a plain-HTTP
 * dev server over a LAN address is not, so there is a fallback. Neither needs
 * to be unguessable - the id never leaves this browser - only distinct from the
 * provider slugs and from the other cards.
 */
const newCardId = (): ChargeCardId => {
  const random = globalThis.crypto
  if (random !== undefined && typeof random.randomUUID === 'function')
    return `own-${random.randomUUID()}`
  return `own-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export interface CardDraft {
  readonly name: string
  readonly operators: readonly OperatorSlug[]
}

interface CardStore {
  readonly wallet: ChargeCardWallet
  /** Define a card. Returns its id, so the panel can scroll to it. */
  addCard(draft: CardDraft): ChargeCardId
  /** Rename a card and re-point it at a set of brands. */
  updateCard(id: ChargeCardId, draft: CardDraft): void
  /** Put a card away. Reversible for a built-in, final for one of the driver's. */
  removeCard(id: ChargeCardId, builtIn: boolean): void
  /** Forget every local edit to the built-in cards. Keeps the driver's own. */
  restoreDefaults(): void
}

export const useCardStore = create<CardStore>((set, get) => {
  /** Persist first, then publish: a write that threw must not be rendered as saved. */
  const commit = (wallet: ChargeCardWallet): void => {
    writeWallet(wallet)
    set({ wallet })
  }

  return {
    wallet: readWallet(),

    addCard: (draft) => {
      const id = newCardId()
      const { wallet } = get()
      commit({
        ...wallet,
        custom: [...wallet.custom, { id, name: draft.name, operators: [...draft.operators] }],
      })
      return id
    },

    updateCard: (id, draft) => {
      const { wallet } = get()
      const own = wallet.custom.some((card) => card.id === id)

      if (own) {
        commit({
          ...wallet,
          custom: wallet.custom.map((card) =>
            card.id === id ? { ...card, name: draft.name, operators: [...draft.operators] } : card,
          ),
        })
        return
      }

      // A built-in card: stored as a patch, so a registry update still reaches
      // the half of the card the driver did not change.
      commit({
        ...wallet,
        overrides: {
          ...wallet.overrides,
          [id]: { name: draft.name, operators: [...draft.operators] },
        },
      })
    },

    removeCard: (id, builtIn) => {
      const { wallet } = get()
      if (!builtIn) {
        commit({ ...wallet, custom: wallet.custom.filter((card) => card.id !== id) })
        return
      }
      commit({ ...wallet, overrides: { ...wallet.overrides, [id]: { deleted: true } } })
    },

    restoreDefaults: () => commit({ ...get().wallet, overrides: {} }),
  }
})

/**
 * The cards the panel shows, built-ins first.
 *
 * Memoised on the wallet, not recomputed per render: the list is a dependency
 * of the detail rail's shortcut list and of the filter panel's pressed states,
 * and a fresh array on every keystroke in the search box would invalidate both.
 */
export const useChargeCards = (): readonly ChargeCard[] => {
  const wallet = useCardStore((store) => store.wallet)
  return useMemo(() => mergeChargeCards(wallet), [wallet])
}

/** Whether there is anything for the restore action to undo. */
export const useHasBuiltInEdits = (): boolean =>
  useCardStore((store) => hasBuiltInEdits(store.wallet))

/** Test seam: drops the wallet in memory and on disk. */
export const resetCardStore = (): void => {
  try {
    storage()?.removeItem(STORAGE_KEY)
  } catch {
    // Nothing stored, nothing to drop.
  }
  useCardStore.setState({ wallet: EMPTY_WALLET })
}
